// 充足率 (= 入札後カバー量 / 調達必要量) の状態判定と表示色。
//
// design `design/js/coverage-state.js` (`ebalanceCoverageState`) の移植で、 spot / intraday の
// tooltip (`PositionChart`) と accordion の充足率バッジ (`summarizeCoverage` /
// `summarizeIntradayCoverage`) が共有する**単一ソース**。 判定を各所に写すと、 過去に
// tooltip とバッジで別々の閾値になった (= 同じコマが tooltip では「余剰」 なのにバッジは
// 警告色) ため、 pct → {key, tone} の写像はこのファイルだけが持つ。
//
// spec: docs/specs/bidding/spot-bidding.md AC-BSP-016 / AC-BSP-016b
//       docs/specs/bidding/intraday-bidding.md AC-BIN-012a
//       docs/adr/bidding-position-need-bilateral-baseline.md
//       (spec PR #4122 / issue #3230、 2026-07-28 merged)

/**
 * 充足率の状態 tone。 design `coverage-state.js` の cls と 1:1:
 * `base` = cov-base (既定色) / `positive` = cov-pos / `warning` = cov-warn /
 * `neutral` = 算出不能 (need 未設定・未入札の placeholder)。
 *
 * 旧 `negative` (= 段階色の「不足が深い」) は 2026-07-28 の spec 改訂で廃止した:
 * 「50% 台の不足」 と 「208% の過剰」 が同じ警告色になり spec と両立しないため。
 */
export type CoverageTone = 'base' | 'positive' | 'warning' | 'neutral'

/**
 * 充足率の状態 (= 状態 key + tone)。
 *
 * 色だけに意味を持たせないため、 表示側は **必ず語を併記する** (WCAG 1.4.1)。
 * 語そのものはここでは持たず `page:biddingCommon.coverageState.<key>` を引く。
 * 以前は日本語を直値で返しており、 EN ロケールでも日本語が出ていた
 * (2026-09-04 レビュー指摘)。
 */
export type CoverageState = {
  key: 'none' | 'reverse' | 'overSell' | 'over' | 'even' | 'short'
  tone: CoverageTone
}

/**
 * 充足率 pct (%) と調達必要量 need から状態を返す。
 *
 * 2026-07-28 の spec 改訂で入札後カバー量の 100% クランプが廃止され、 色は「不足の深さ」
 * ではなく「過剰・逆方向かどうか」で切り替える 5 状態になった:
 *
 *   逆方向   (pct < 0)                … 買うべきコマに売り札。 二度打ち / 方向ミス → 警告色
 *   不足     (0 ≤ pct < 100)          … 調達途中の正常な状態 → 既定色
 *   均衡     (pct = 100)              … 過不足なし → positive
 *   買い過剰 (pct > 100 かつ need ≥ 0) … 余剰インバランスの焼き込みリスク → 警告色
 *   売り過剰 (pct > 100 かつ need < 0) … 売るべき余剰を超えて売った = 自社に不足が出る → 警告色
 *     (#9067、 2026-10-10 PdM 判断。 旧: 「余剰」 = 売り切っている = 正常 の既定色。
 *     `docs/specs/conventions.md` §入札後ポジションの量の向き、 8080 `js/coverage-state.js`)
 *
 * need の符号規約は `go/internal/backend/domain/entity/supply/gap_compute.go`
 * `ComputeProcurementNeed` godoc 準拠 (正 = 買うべき量(不足) / 負 = 売れる余剰)。
 */
export const resolveCoverageState = (
  pct: number | undefined,
  needMw: number,
): CoverageState => {
  if (pct === undefined || !Number.isFinite(pct)) {
    return { key: 'none', tone: 'neutral' }
  }
  if (pct < 0) return { key: 'reverse', tone: 'warning' }
  if (pct > 100) {
    return needMw < 0
      ? { key: 'overSell', tone: 'warning' }
      : { key: 'over', tone: 'warning' }
  }
  if (pct === 100) return { key: 'even', tone: 'positive' }
  return { key: 'short', tone: 'base' }
}

/**
 * 入札後残量 MW (AC-BIN-012a / AC-BSP-016b、 `docs/specs/conventions.md` §入札後ポジションの量の向き)。
 *
 * |調達必要量| − 入札時カバー量を調達必要量の向き (不足 = 買い / 余剰 = 売り) にそろえた量。
 * プラスはまだ残っている量、 マイナスは調達必要量を超えて出した量で、 買い・売りのどちらの
 * コマでも同じ意味になる (#9067)。 例: 調達必要量 −100 MW のコマに売り 150 MW (cover −150) → −50。
 * need = 0 は買いの向き (need − cover) で扱う (8080 `ebalanceCoverageRemain` と同じ)。
 */
export const resolveRemainMw = (coverMw: number, needMw: number): number =>
  needMw >= 0 ? needMw - coverMw : coverMw - needMw

export type CoverageBadgeColors = {
  /** 充足率テキスト (= 「充足率 67%」) の色。 */
  badgeColor: string
  /** accordion item 自体の背景色 (= 業務状態の色付け)。 */
  accordionBg: string
}

/**
 * tone → Chakra theme token (= 色キー)。 design tokens に未昇格のため文字列直書き。
 *
 * design `applyCoverageClasses` と同じく、 accordion に状態色を付けるのは
 * positive / warning のみ (= base と neutral は素の背景のまま)。
 */
export const coverageBadgeColorsOf = (
  tone: CoverageTone,
): CoverageBadgeColors => {
  switch (tone) {
    case 'positive':
      return { badgeColor: 'green.700', accordionBg: 'green.50' }
    case 'warning':
      return { badgeColor: 'orange.700', accordionBg: 'orange.50' }
    case 'base':
      return { badgeColor: 'Text.highEmphasis', accordionBg: 'white' }
    case 'neutral':
    default:
      return { badgeColor: 'gray.500', accordionBg: 'white' }
  }
}
